// A mind map on paper (design-docs/Maps — design.md ▸ The look, Using it): one SVG, the paper in the map's own tokens
// (charcoal on Paper, chalk on slate in Lamplight; crisp and grainless in high contrast), regions as smudged washes,
// lines and outlines as the filled ribbons of `MapSketch` rubbed through a grain filter, and the notes as HTML text in
// `foreignObject`s, measured once drawn so the lines meet them at their edges (`MapLayout`). Pan and zoom are one
// transform on the root group.
//
// Using it: double-click the paper for a note; double-click a note, or Space, to edit it (Return ends, Shift-Return a
// new line, Tab ends and makes a child, Escape ends); Tab makes a child to the right and Return a sibling below, each
// joined and being edited; Delete removes; ⌥-arrows step to the joined note that way; the arrows go through the notes in
// reading order. Click selects, Shift- or ⌘-click adds; drag the paper for a marquee; drag notes to move them (⌥-drag
// moves the branch); drag the knob of a selected note to another note to join them, or onto the paper for a new joined
// note. The style bar by the selection (and the context menu) sets ink, outline, size, bold and face, a line's arrows,
// dots and label, and the centre. ⌘Z / ⇧⌘Z undo and redo within the map; ⌘0 fits, ⌘+ and ⌘− zoom; ⌘-wheel or a pinch
// zooms, the wheel or one finger pans; a long press then drag on touch is a marquee.
//
// Ink (phase 4): with the header's Pen on, dragging on the paper draws (a mouse or a finger; a pen always draws). A
// finished stroke is read by `MapInk.classify`: drawn from one note to another it joins them, round notes it selects
// them, scribbled over a note it scratches it out; anything else is kept as a stroke in the pen's ink. Strokes are chosen
// by a click (Shift adds) or the marquee, dragged with the chosen notes, and deleted with them. There's no reading of
// handwriting on the web: strokes stay ink.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import {
  emptyMapContent, entityCSSColor, MAP_AUTO_WIDTH, MapArrange, MapCast, MapInk, MapLayout, mapNote, mapRegion, MapScenes, MapSketch, mapStroke, placedStroke,
  readMapContent, SwiftReader, type InkColor, type MapStroke, type MapStrokePoint, type MapArrow, type MapContent, type MapDirection, type MapDocument, type MapFace, type MapFrame, type MapLine, type MapLink, type MapNote,
  type MapNoteSize, type MapOutline, type MapPoint, type MapRegion, type MapScenesItem, type OpenedProject, type Project, type SketchMark,
} from '@writing-suite/core';
import { announce } from '../../shell/context.ts';
import { useMapSteadyLines } from '../../shell/preferences.ts';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { navigate } from '../../shell/route.ts';
import { useWorkspace } from '../../shell/workspace.ts';
import { documentNoun } from '../../store/binderFilter.ts';
import { saveDownload } from '../../store/exporting.ts';
import { mapEdit, MapUndo, onMapCommand, publishMapZoom, takeCentreEdit, takeImported, useMapPen, type MapCommand } from '../../store/maps.ts';
import { planEdit } from '../../store/plan.ts';
import type { LiveProject } from '../../store/project.ts';
import { INKS } from '../FormatBar.tsx';
import { linkableDocuments } from '../Links.tsx';
import { MenuButton, MenuPopup, type MenuEntry } from '../Menu.tsx';
import { usePhone } from '../plan/hooks.ts';
import { Icon } from '../Icons.tsx';
import { EntityDot } from '../story/common.tsx';
import {
  clampZoom, edgeToward, fitView, inMarquee, linkShown, markPath, midPoint, noteLabel, noteWords, pointerPressure, polylinePoints, strokeMarkPoints, strokeOffsets,
  strokesInMarquee, toPaper, unionFrame, zoomAbout, type MapView,
} from './mapDraw.ts';
import { LinkCastDialog, MapPickerDialog, TurnIntoScenesDialog, type ScenesPlace } from './MapDialogs.tsx';
import { exportFileName, mapPDF, mapPNG } from './mapExport.ts';
import {
  between, cardInfo, castChoices, castOf, chapterLabels, dropPlaces, linkLives, manuscriptFolders, placeToRight, REGION_MIN, shownPlaces, stackHeads, stackLine,
  staleTitles, type CardInfo, type MapChoice,
} from './mapProject.ts';

export interface MapCanvasProps {
  project: Project;
  live: LiveProject | null;
  fragmentId: string;
  title: string;
  /** The map can be changed here (writable, not locked, not in the Trash). */
  editable: boolean;
  hrefFor: PageProps['hrefFor'];
}

/** A new note's size until it's measured (Tab and Return place it with this). */
const NEW_SIZE = { width: 64, height: 24 };
/** Far out, a note shows its first line; close in, a card its synopsis. */
const FAR = 0.6;
const NEAR = 1.25;

const OUTLINES: { value: MapOutline; name: string }[] = [
  { value: 'none', name: 'None' }, { value: 'oval', name: 'Oval' }, { value: 'box', name: 'Box' }, { value: 'cloud', name: 'Cloud' },
  { value: 'underline', name: 'Underline' },
];
const SIZES: { value: MapNoteSize; name: string; short: string }[] = [
  { value: 'small', name: 'Small', short: 'S' }, { value: 'normal', name: 'Normal', short: 'M' }, { value: 'large', name: 'Large', short: 'L' },
];
const ARROWS: { value: MapArrow; name: string }[] = [{ value: 'none', name: 'No Arrow' }, { value: 'end', name: 'Arrow at the End' }, { value: 'both', name: 'Arrows at Both Ends' }];

/** What's chosen: notes, lines, regions (chosen on the paper inside one), or strokes of ink (with notes or alone). */
interface Selection { notes: string[]; lines: string[]; regions?: string[]; strokes?: string[] }
const NOTHING: Selection = { notes: [], lines: [] };
/** On screen, how near a click must come to a stroke to choose it. */
const STROKE_REACH = 6;
/** Ink drawn with the pen: 2 points wide. */
const PEN_WIDTH = 2;

/** The contents' (and the corkboard's) drag of documents. */
const FRAGMENTS_TYPE = 'application/x-writing-suite-fragments';
/** How long Arrange Around Centre and Tidy Branch take to move the notes. */
const ARRANGE_MS = 300;

const reducedMotion = () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

interface Editing { id: string; isNew: boolean; selectAll: boolean; group: string; key: number }

type Gesture =
  /** Notes (`ids`) and strokes dragged; `id` the note, or the stroke (`stroke`), the drag began on. */
  | { kind: 'move'; id: string; ids: string[]; strokes: string[]; stroke?: boolean; start: { x: number; y: number }; moved: boolean; additive: boolean }
  | { kind: 'marquee'; a: { x: number; y: number }; base: string[]; baseStrokes: string[] }
  | { kind: 'ink'; pointerId: number }
  | { kind: 'region'; id: string; notes: string[]; start: { x: number; y: number }; moved: boolean }
  | { kind: 'resize'; id: string; start: { x: number; y: number }; width: number; height: number }
  | { kind: 'pan'; start: { x: number; y: number }; view: MapView }
  | { kind: 'touch'; start: { x: number; y: number }; view: MapView; timer: ReturnType<typeof setTimeout>; marquee: boolean }
  | { kind: 'connect'; from: string; start: { x: number; y: number } }
  | { kind: 'pinch'; distance: number; mid: { x: number; y: number }; view: MapView };

const inkClass = (ink: InkColor | null | undefined) => (ink ? ` tint-${ink}` : '');
const newID = () => crypto.randomUUID().toLowerCase();
const message = (err: unknown) => (err instanceof Error ? err.message : String(err));

export function MapCanvas({ project, live, fragmentId, title, editable, hrefFor }: MapCanvasProps) {
  const ws = useWorkspace();
  const phone = usePhone();
  const steady = useMapSteadyLines();
  const pen = useMapPen();
  const style = steady ? MapSketch.steady : MapSketch.defaultStyle;
  const docName = `fragment.${fragmentId}`;

  // What the map holds: read again whenever its document changes (here, or arriving from another device).
  const [, setTick] = useState(0);
  const bump = () => setTick((t) => t + 1);
  useEffect(() => live?.onRemote((name) => void (name === docName && bump())), [live, docName]);
  const doc = live?.doc(docName) ?? (project as OpenedProject).fragmentDoc?.(fragmentId) ?? null;
  const content = useMemo(() => (doc ? readMapContent(new SwiftReader(doc)) : emptyMapContent()), [doc]);
  const current = (): MapContent => {
    const d = live?.doc(docName);
    return d ? readMapContent(new SwiftReader(d)) : content;
  };

  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [view, setViewState] = useState<MapView>({ x: 0, y: 0, k: 1 });
  const viewRef = useRef(view);
  const setView = (v: MapView) => {
    viewRef.current = v;
    setViewState(v);
  };
  const [box, setBox] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const measure = () => setBox((b) => (b.width === el.clientWidth && b.height === el.clientHeight ? b : { width: el.clientWidth, height: el.clientHeight }));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Each note's size as drawn (its text's box, in canvas points).
  const [sizes, setSizes] = useState<Map<string, { w: number; h: number }>>(() => new Map());
  const noteEls = useRef(new Map<string, HTMLElement>());
  const observer = useMemo(
    () =>
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver((entries) =>
            setSizes((old) => {
              let next: Map<string, { w: number; h: number }> | null = null;
              for (const e of entries) {
                const el = e.target as HTMLElement;
                const id = el.dataset.noteId;
                if (!id || !el.isConnected) continue;
                const w = el.offsetWidth, h = el.offsetHeight;
                const was = old.get(id);
                if (was && was.w === w && was.h === h) continue;
                next ??= new Map(old);
                next.set(id, { w, h });
              }
              return next ?? old;
            }),
          ),
    [],
  );
  useEffect(() => () => observer?.disconnect(), [observer]);
  const register = useCallback(
    // Refs are called again on every render (null, then the element): only a new element is observed; a note's element
    // is forgotten when the note goes (below).
    (id: string) => (el: HTMLElement | null) => {
      const had = noteEls.current.get(id);
      if (!el || el === had) return;
      if (had) observer?.unobserve(had);
      noteEls.current.set(id, el);
      observer?.observe(el);
    },
    [observer],
  );

  const [selection, setSelection] = useState<Selection>(NOTHING);
  const [editing, setEditingState] = useState<Editing | null>(null);
  const editingRef = useRef<Editing | null>(null);
  const setEditing = (e: Editing | null) => {
    editingRef.current = e;
    setEditingState(e);
  };
  /** Notes (and a region, or strokes) being dragged, by how far. */
  const [drag, setDrag] = useState<{ ids: Set<string>; dx: number; dy: number; region?: string; strokes?: Set<string> } | null>(null);
  /** The stroke being drawn with the pen, in canvas points. */
  const [inking, setInkingState] = useState<MapStrokePoint[] | null>(null);
  const inkingRef = useRef<MapStrokePoint[] | null>(null);
  const setInking = (points: MapStrokePoint[] | null) => {
    inkingRef.current = points;
    setInkingState(points);
  };
  const [resize, setResize] = useState<{ id: string; width: number; height: number } | null>(null);
  /** Arrange Around Centre or Tidy Branch on its way: the notes' places before, after, and how far along. */
  const [anim, setAnim] = useState<{ from: Map<string, MapPoint>; to: Map<string, MapPoint>; t: number } | null>(null);
  const [dialog, setDialog] = useState<
    | { kind: 'add'; at: MapPoint | null }
    | { kind: 'link'; note: string }
    | { kind: 'cast' }
    | { kind: 'scenes'; notes: string[] }
    | null
  >(null);
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tell = (text: string) => {
    announce(text);
    setNotice(text);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 6000);
  };
  useEffect(() => () => void (noticeTimer.current && clearTimeout(noticeTimer.current)), []);
  const [marquee, setMarquee] = useState<{ a: { x: number; y: number }; b: { x: number; y: number }; base: string[]; baseStrokes: string[] } | null>(null);
  const [connect, setConnect] = useState<{ from: string; to: { x: number; y: number } } | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [labeling, setLabeling] = useState<string | null>(null);
  const history = useRef(new MapUndo());
  const gesture = useRef<Gesture | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number; touch: boolean }>());
  const pointerFocus = useRef(false);
  const editKey = useRef(0);

  // Ids that went (a note deleted here or on another device) leave the selection.
  useEffect(() => {
    const notes = new Set(content.notes.map((n) => n.id)), lines = new Set(content.lines.map((l) => l.id));
    const strokes = new Set(content.strokes.map((x) => x.id)), regions = new Set(content.regions.map((r) => r.id));
    setSelection((s) => {
      const next: Selection = {
        notes: s.notes.filter((id) => notes.has(id)), lines: s.lines.filter((id) => lines.has(id)),
        ...(s.regions ? { regions: s.regions.filter((id) => regions.has(id)) } : {}),
        ...(s.strokes ? { strokes: s.strokes.filter((id) => strokes.has(id)) } : {}),
      };
      const same = next.notes.length === s.notes.length && next.lines.length === s.lines.length && next.regions?.length === s.regions?.length
        && next.strokes?.length === s.strokes?.length;
      return same ? s : next;
    });
    if (editingRef.current && !notes.has(editingRef.current.id)) setEditing(null);
    for (const [id, el] of noteEls.current) {
      if (notes.has(id)) continue;
      observer?.unobserve(el);
      noteEls.current.delete(id);
    }
  }, [content]); // eslint-disable-line react-hooks/exhaustive-deps

  /** What a note links to, while it's there: a document's card or the cast's chip (else a plain note). */
  const linkKind = (n: MapNote): 'fragment' | 'entity' | null => (n.link && linkLives(project, n.link) ? n.link.kind : null);
  /** A document card is drawn in a pencil box, whatever its outline, and a chip has none; lines meet them there. */
  const outlineOf = (n: MapNote): MapOutline => (linkKind(n) === 'fragment' ? 'box' : linkKind(n) === 'entity' ? 'none' : n.outline);
  /** The map with each linked note's outline as drawn (a card's box, a chip's none), for the room notes take. */
  const asDrawn = (c: MapContent): MapContent => ({ ...c, notes: c.notes.map((n) => (n.link ? { ...n, outline: outlineOf(n) } : n)) });
  // Where everything is drawn: stacked notes under their head, the notes being dragged (or arranged) where the pointer
  // (or the animation) has them, and a region being moved or resized.
  const measured = useMemo(() => new Map([...sizes].map(([id, s]) => [id, { width: s.w, height: s.h }])), [sizes]);
  /** Places as they are, with no drag or animation: what arranging and stacking start from. */
  const rest = useMemo(() => shownPlaces(asDrawn(content), measured), [content, measured, project]); // eslint-disable-line react-hooks/exhaustive-deps
  const drawn = useMemo((): MapContent => {
    const animated = anim ? between(anim.from, anim.to, anim.t) : null;
    const base = animated ? { ...content, notes: content.notes.map((n) => (animated.has(n.id) ? { ...n, at: animated.get(n.id)! } : n)) } : content;
    const notes = !drag && !animated ? rest.notes : shownPlaces(asDrawn(base), measured, drag ?? undefined).notes;
    const regions = drag?.region || resize
      ? content.regions.map((r) => (r.id === drag?.region ? { ...r, at: { x: r.at.x + drag.dx, y: r.at.y + drag.dy } } : r.id === resize?.id ? { ...r, width: resize.width, height: resize.height } : r))
      : content.regions;
    return { ...content, notes, regions };
  }, [content, drag, anim, resize, rest, measured]);
  const frames = useMemo(() => {
    const out = new Map<string, MapFrame>();
    for (const n of drawn.notes) {
      const s = sizes.get(n.id);
      out.set(n.id, { x: n.at.x, y: n.at.y, width: s?.w ?? NEW_SIZE.width, height: s?.h ?? NEW_SIZE.height });
    }
    return out;
  }, [drawn, sizes]);
  const forEnds = useMemo(() => ({ ...drawn, notes: drawn.notes.map((n) => (n.link ? { ...n, outline: outlineOf(n) } : n)) }), [drawn, project]); // eslint-disable-line react-hooks/exhaustive-deps
  const chapters = useMemo(() => chapterLabels(project), [project]);
  const order = useMemo(() => MapLayout.readingOrder(content), [content]);
  const byID = useMemo(() => new Map(drawn.notes.map((n) => [n.id, n])), [drawn]);
  const words = (n: MapNote) => noteWords(project, n);

  // MARK: The view

  const fitted = useRef(false);
  const fit = (most = 1.5) => setView(fitView(MapLayout.bounds(content, frames), box.width, box.height, most));
  // Opened: the whole map in view, never larger than life, once every note is measured.
  useEffect(() => {
    if (fitted.current || !box.width || !box.height) return;
    if (content.notes.some((n) => !sizes.has(n.id))) return;
    fitted.current = true;
    fit(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box, sizes, content]);
  useEffect(() => publishMapZoom(fragmentId, view.k), [fragmentId, view.k]);
  const zoomBy = (factor: number) => setView(zoomAbout(viewRef.current, viewRef.current.k * factor, box.width / 2, box.height / 2));
  const command = (c: MapCommand) => {
    if (c === 'in') zoomBy(1.25);
    else if (c === 'out') zoomBy(1 / 1.25);
    else if (c === 'actual') setView(zoomAbout(viewRef.current, 1, box.width / 2, box.height / 2));
    else if (c === 'add') editable && setDialog({ kind: 'add', at: null });
    else if (c === 'cast') editable && setDialog({ kind: 'cast' });
    else if (c === 'arrange') arrangeAroundCentre();
    else if (c === 'png' || c === 'pdf') void exportAs(c);
    else fit();
  };
  const commandRef = useRef(command);
  commandRef.current = command;
  useEffect(() => onMapCommand(fragmentId, (c) => commandRef.current(c)), [fragmentId]);

  /** Brings a note into view if it's off the paper's visible part. */
  const ensureVisible = (id: string) => {
    const f = frames.get(id);
    if (!f || !box.width) return;
    const v = viewRef.current, m = 40;
    const x0 = f.x * v.k + v.x, y0 = f.y * v.k + v.y, x1 = x0 + Math.max(f.width, 120) * v.k, y1 = y0 + f.height * v.k;
    let dx = 0, dy = 0;
    if (x1 > box.width - m) dx = box.width - m - x1;
    if (x0 + dx < m) dx = m - x0;
    if (y1 > box.height - m) dy = box.height - m - y1;
    if (y0 + dy < m) dy = m - y0;
    if (dx || dy) setView({ ...v, x: v.x + dx, y: v.y + dy });
  };

  // Wheel: ⌘ or Ctrl (a trackpad's pinch) zooms about the pointer; otherwise it pans. Native, so it can be stopped.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? r.height : 1;
      const v = viewRef.current;
      if (e.ctrlKey || e.metaKey) setView(zoomAbout(v, v.k * Math.exp(-e.deltaY * unit * 0.01), e.clientX - r.left, e.clientY - r.top));
      else setView({ ...v, x: v.x - e.deltaX * unit, y: v.y - e.deltaY * unit });
    };
    el.addEventListener('wheel', wheel, { passive: false });
    return () => el.removeEventListener('wheel', wheel);
  }, []);

  // MARK: Edits

  /** One change to the map, kept for undo (`group`: one step for several). False when it was refused (a lock). */
  const apply = (fn: (m: MapDocument) => void, group: string | null = null): boolean => {
    if (!live || !editable) return false;
    const before = current();
    try {
      mapEdit(live, fragmentId, fn);
    } catch (err) {
      announce(message(err));
      return false;
    }
    history.current.record(before, current(), group);
    bump();
    return true;
  };

  const undo = (redo = false) => {
    if (!live || !editable) return;
    if (editingRef.current) finishEdit('escape');
    const now = current();
    const state = redo ? history.current.redo(now) : history.current.undo(now);
    if (!state) return announce(redo ? 'Nothing to redo' : 'Nothing to undo');
    try {
      mapEdit(live, fragmentId, (m) => m.replaceMapContent(state));
    } catch (err) {
      announce(message(err));
    }
    bump();
  };

  const select = (s: Selection, focus = false) => {
    setSelection(s);
    if (focus && s.notes.length) {
      const id = s.notes[s.notes.length - 1];
      requestAnimationFrame(() => {
        noteEls.current.get(id)?.focus({ preventScroll: true });
        ensureVisible(id);
      });
    }
  };

  /** A new note at `at`, joined from `joinTo`, being edited. */
  const createNote = (at: { x: number; y: number }, joinTo: string | null) => {
    const id = newID();
    const group = `note:${id}`;
    const made = apply((m) => {
      m.addMapNote(mapNote({ id, at, face: content.face }));
      if (joinTo) m.connectMapNotes(joinTo, id);
    }, group);
    if (!made) return;
    setSelection({ notes: [id], lines: [] });
    setEditing({ id, isNew: true, selectAll: false, group, key: ++editKey.current });
    requestAnimationFrame(() => ensureVisible(id));
  };

  const makeChild = (parent: string) => {
    if (!editable) return;
    createNote(MapLayout.childOrigin(parent, NEW_SIZE, asDrawn(current()), frames), parent);
  };

  const makeSibling = (of: string) => {
    if (!editable) return;
    createNote(MapLayout.siblingOrigin(of, NEW_SIZE, asDrawn(current()), frames), MapLayout.parent(of, current()));
  };

  const startEdit = (id: string, selectAll = false) => {
    const note = byID.get(id);
    if (!editable || !note || linkKind(note)) return;
    setSelection({ notes: [id], lines: [] });
    setEditing({ id, isNew: false, selectAll, group: `text:${id}:${++editKey.current}`, key: editKey.current });
  };

  const typed = useRef<{ text: string; timer: ReturnType<typeof setTimeout> | null } | null>(null);
  const commitText = (e: Editing, text: string) => {
    const note = current().notes.find((n) => n.id === e.id);
    if (!note || note.text === text) return;
    apply((m) => m.setMapNoteText(e.id, text), e.group);
  };
  const onTyping = (text: string) => {
    const e = editingRef.current;
    if (!e) return;
    if (typed.current?.timer) clearTimeout(typed.current.timer);
    typed.current = { text, timer: setTimeout(() => commitText(e, text), 600) };
  };

  /** Ends the edit: the words kept (an emptied new note taken away), then Tab's child, or the note focused. */
  const finishEdit = (how: 'return' | 'tab' | 'escape' | 'blur', text?: string) => {
    const e = editingRef.current;
    if (!e) return;
    setEditing(null);
    const words = text ?? typed.current?.text ?? null;
    if (typed.current?.timer) clearTimeout(typed.current.timer);
    typed.current = null;
    if (words !== null) commitText(e, words);
    const note = current().notes.find((n) => n.id === e.id);
    if (e.isNew && note && note.text.trim() === '') {
      apply((m) => m.removeMapNotes([e.id]), e.group);
      history.current.dropIfSame(current());
      setSelection(NOTHING);
      requestAnimationFrame(() => wrapRef.current?.focus({ preventScroll: true }));
      return;
    }
    history.current.close();
    if (how === 'tab' && note) makeChild(e.id);
    else if (how !== 'blur') {
      // The canvas takes the keys at once (the note's button is drawn on the next frame).
      wrapRef.current?.focus({ preventScroll: true });
      requestAnimationFrame(() => {
        // Unless a key meanwhile began another note.
        if (document.activeElement === wrapRef.current) noteEls.current.get(e.id)?.focus({ preventScroll: true });
      });
    }
  };

  const removeSelection = () => {
    const { notes, lines } = selection;
    const regions = selection.regions ?? [];
    const strokes = selection.strokes ?? [];
    if (!notes.length && !lines.length && !regions.length && !strokes.length) return;
    if (apply((m) => {
      m.removeMapLines(lines);
      m.removeMapNotes(notes);
      m.removeMapRegions(regions);
      m.removeMapStrokes(strokes);
    })) {
      announce(notes.length ? (notes.length === 1 ? 'Note deleted' : `${notes.length} notes deleted`) : lines.length ? (lines.length === 1 ? 'Line rubbed out' : 'Lines rubbed out')
        : regions.length ? 'Region deleted' : 'Ink rubbed out');
      setSelection(NOTHING);
      wrapRef.current?.focus({ preventScroll: true });
    }
  };

  const setNotes = (fn: (m: MapDocument, id: string) => void) => apply((m) => selection.notes.forEach((id) => fn(m, id)));
  const setLines = (fn: (m: MapDocument, id: string) => void) => apply((m) => selection.lines.forEach((id) => fn(m, id)));
  const chooseFace = (face: MapFace) => apply((m) => {
    selection.notes.forEach((id) => m.setMapNote(id, { face }));
    m.setMapFace(face);
  });
  const makeCentre = (id: string) => apply((m) => {
    m.setMapCentre(id);
    m.setMapNote(id, { outline: 'oval' });
    m.setMapNote(id, { size: 'large' });
  });
  const clearCentre = () => apply((m) => m.setMapCentre(null));

  // MARK: The project on the map

  // A linked note keeps the title as last seen: written again (silently, no undo step) when the document's title or the
  // entity's name differs.
  useEffect(() => {
    if (!live || !editable || editingRef.current) return;
    const stale = staleTitles(project, content);
    if (!stale.size) return;
    try {
      mapEdit(live, fragmentId, (m) => stale.forEach((title, id) => m.setMapNoteText(id, title)));
      bump();
    } catch {
      // A lock or a read-only map: the titles shown are the live ones anyway.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, content, editable]);

  const linkOf = (c: MapChoice): MapLink => ({ kind: c.kind, id: c.id });
  /** Notes for documents or the cast at these places, selected: cards outlined as boxes, chips without. */
  const addLinked = (choices: MapChoice[], places: MapPoint[]) => {
    const made = choices.map((c, i) => mapNote({ text: c.title, at: places[i], link: linkOf(c), outline: c.kind === 'fragment' ? 'box' : 'none', face: content.face }));
    if (!made.length || !apply((m) => made.forEach((n) => m.addMapNote(n)))) return;
    setSelection({ notes: made.map((n) => n.id), lines: [] });
    announce(made.length === 1 ? `“${choices[0].title}” added to the map` : `${made.length} added to the map`);
    requestAnimationFrame(() => ensureVisible(made[0].id));
  };
  const addChoice = (c: MapChoice, at: MapPoint | null) =>
    addLinked([c], [at ?? placeToRight(asDrawn(current()), frames, { width: Math.max(NEW_SIZE.width, c.title.length * 8), height: NEW_SIZE.height })]);
  /** Link…: the note stands for the choice; its words become the title only if it had none. */
  const linkNote = (id: string, c: MapChoice) => {
    const note = current().notes.find((n) => n.id === id);
    if (!note) return;
    if (apply((m) => {
      m.setMapNote(id, { link: linkOf(c) });
      if (note.text.trim() === '') m.setMapNoteText(id, c.title);
    })) announce(`Linked to “${c.title}”`);
  };
  const unlinkNote = (id: string) => {
    if (apply((m) => m.setMapNote(id, { link: null }))) announce('Unlinked');
  };
  const castMatches = () => {
    const entities = castOf(project);
    return MapCast.matches(current(), entities).map(({ note, entity }) => {
      const e = entities.find((x) => x.id === entity)!;
      return { note, entity, words: (current().notes.find((n) => n.id === note)?.text ?? '').trim(), name: e.name, color: e.color ?? null };
    });
  };
  const linkCast = (pairs: { note: string; entity: string }[]) => {
    if (!pairs.length) return;
    if (apply((m) => pairs.forEach((p) => {
      m.setMapNote(p.note, { link: { kind: 'entity', id: p.entity } });
      m.setMapNote(p.note, { outline: 'none' });
    }))) tell(pairs.length === 1 ? 'Linked 1 note to the cast.' : `Linked ${pairs.length} notes to the cast.`);
  };

  /** Moves notes to new places as one step, drawn moving there over a moment (at once with Reduce Motion). */
  const moveAnimated = (places: Map<string, MapPoint>) => {
    if (!places.size) return false;
    const from = new Map([...places.keys()].map((id) => [id, rest.frames.get(id) ?? { x: 0, y: 0 }] as const));
    const fromPoints = new Map([...from].map(([id, f]) => [id, { x: f.x, y: f.y }]));
    if (!apply((m) => m.moveMapNotes(places))) return false;
    if (reducedMotion()) return true;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / ARRANGE_MS);
      setAnim(t < 1 ? { from: fromPoints, to: places, t } : null);
      if (t < 1) requestAnimationFrame(step);
    };
    setAnim({ from: fromPoints, to: places, t: 0 });
    requestAnimationFrame(step);
    return true;
  };
  const arrangeAroundCentre = () => {
    if (!editable) return;
    const c = current();
    if (!c.centre) return announce('This map has no centre');
    if (moveAnimated(MapArrange.arrangeAroundCentre(asDrawn(c), rest.frames))) announce('Arranged around the centre');
  };
  const tidyBranch = (id: string) => {
    if (moveAnimated(MapArrange.tidyBranch(id, asDrawn(current()), rest.frames))) announce('Branch tidied');
  };
  /** Whether new places move any note from where it's drawn (Tidy Branch is offered only then). */
  const wouldMove = (places: Map<string, MapPoint>) =>
    [...places].some(([id, p]) => { const f = rest.frames.get(id); return !f || Math.abs(f.x - p.x) > 0.5 || Math.abs(f.y - p.y) > 0.5; });
  const selectBranch = (id: string) => select({ notes: [...MapLayout.branch(id, content)], lines: [] });

  const heads = useMemo(() => stackHeads(content), [content]);
  const stacksNow = useMemo(() => MapArrange.stacks(content), [content]);
  /** Stack: the notes in one column under a head; they're drawn there, and their places written there too. */
  const stackNotes = (ids: string[]) => {
    const c = current();
    const made = MapArrange.stacking(ids, c, rest.frames);
    const drawnNow = asDrawn(c);
    if (!made) return;
    const after: MapContent = { ...drawnNow, notes: drawnNow.notes.map((n) => {
      const m = made.members.find((x) => x.id === n.id);
      return m ? { ...n, stack: made.head, stackOrder: m.order } : n;
    }) };
    const places = MapArrange.stackPlaces(after, rest.frames);
    if (apply((m) => {
      for (const { id, order } of made.members) m.setMapNote(id, { stack: made.head, order });
      m.moveMapNotes(new Map(made.members.flatMap(({ id }) => (places.has(id) ? [[id, places.get(id)!] as const] : []))));
    })) announce('Stacked');
  };
  /** Unstack: a note in a stack comes out of it; a stack's head lets every note in it go. Each stays where it's drawn. */
  const unstackNote = (id: string) => {
    const out = heads.has(id) ? [id] : (stacksNow.get(id) ?? []);
    if (!out.length) return;
    if (apply((m) => {
      for (const n of out) m.setMapNote(n, { stack: null, order: null });
      m.moveMapNotes(new Map(out.flatMap((n) => { const f = rest.frames.get(n); return f ? [[n, { x: f.x, y: f.y }] as const] : []; })));
    })) announce('Unstacked');
  };

  /** Make Region: a grey wash round the room the chosen notes take (outlines and stacks included), chosen itself. */
  const makeRegion = (ids: string[]) => {
    const drawnNow = asDrawn(current());
    const frame = MapArrange.regionFrame(ids.flatMap((id) => MapLayout.occupied(id, drawnNow, rest.frames) ?? []));
    if (!frame) return;
    const region = mapRegion({ at: { x: frame.x, y: frame.y }, width: frame.width, height: frame.height, ink: 'grey' });
    if (apply((m) => m.addMapRegion(region))) {
      setSelection({ notes: [], lines: [], regions: [region.id] });
      announce('Region made');
      wrapRef.current?.focus({ preventScroll: true });
    }
  };
  const setRegions = (fn: (r: MapRegion) => MapRegion) => apply((m) => {
    for (const id of selection.regions ?? []) {
      const r = current().regions.find((x) => x.id === id);
      if (r) m.updateMapRegion(fn(r));
    }
  });

  // MARK: Ink

  /** A stroke finished on the paper (canvas points): from note to note it joins them (the line chosen), round notes it
   *  chooses them, scribbled over a note it scratches it out; anything else is kept as ink in the pen's colour. */
  const finishStroke = (drawn: MapStrokePoint[]) => {
    const points = MapInk.simplified(drawn);
    if (points.length <= 1 && drawn.length !== 1) return;
    const c = current();
    const meant = MapInk.classify(points, asDrawn(c), frames);
    if (meant.kind === 'join') {
      let line = '';
      if (apply((m) => void (line = m.connectMapNotes(meant.a, meant.b)))) {
        setSelection({ notes: [], lines: [line] });
        announce('Joined');
      }
    } else if (meant.kind === 'select') {
      if (editingRef.current) finishEdit('blur');
      setSelection({ notes: meant.ids, lines: [] });
      announce(meant.ids.length === 1 ? '1 note selected' : `${meant.ids.length} notes selected`);
    } else if (meant.kind === 'scratch') {
      if (apply((m) => m.removeMapNotes([meant.id]))) {
        setSelection((s) => ({ ...s, notes: s.notes.filter((n) => n !== meant.id) }));
        announce('Note scratched out');
      }
    } else {
      const stroke = mapStroke({ points, ink: pen.ink, width: PEN_WIDTH });
      apply((m) => m.addMapStroke(stroke));
    }
  };

  /** A stroke's ink is part of it as drawn: the chosen strokes are written again in the new one (as the Apple apps do). */
  const setStrokesInk = (ink: InkColor | null) => {
    const ids = new Set(selection.strokes ?? []);
    const changed = current().strokes.filter((s) => ids.has(s.id)).map((s): MapStroke => ({ ...s, ink }));
    if (!changed.length) return;
    apply((m) => {
      m.removeMapStrokes(ids);
      changed.forEach((s) => m.addMapStroke(s));
    });
  };

  /** Turn Into Scenes…: the documents made, the notes their cards, and where they went said. */
  const turnIntoScenes = (plan: MapScenesItem[], place: ScenesPlace) => {
    setDialog(null);
    if (!live || !editable) return;
    const destination = place.kind === 'folder' ? { folder: place.nodeId } : place.kind === 'top' ? { topLevel: true as const } : { newFolder: place.name };
    const before = current();
    try {
      planEdit(live, (e) => e.turnMapNotesIntoScenes(fragmentId, plan, destination));
    } catch (err) {
      return announce(message(err));
    }
    history.current.record(before, current());
    bump();
    const n = MapScenes.count(plan);
    const noun = documentNoun(project.type, true);
    const manuscript = project.manuscriptId();
    const where = place.kind === 'folder'
      ? manuscriptFolders(project).find((f) => f.nodeId === place.nodeId)?.title ?? 'the manuscript'
      : place.kind === 'new' ? place.name.trim() || 'New Chapter'
      : project.arrangements().find((a) => a.id === manuscript)?.name ?? 'the manuscript';
    tell(`Made ${n} ${noun}${n === 1 ? '' : 's'} in “${where}”.`);
  };

  // A board just imported says what came in, then offers to link notes naming the cast (an offer: nothing is linked until
  // the writer says). Taken once, a moment after mounting, so a remount in development doesn't take it twice.
  useEffect(() => {
    const summary = takeImported(fragmentId);
    if (summary === null) return;
    setTimeout(() => {
      tell(summary);
      if (editable && castMatches().length) setDialog({ kind: 'cast' });
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fragmentId]);

  /** Export as PNG… / PDF…: the whole map on Paper, downloaded as "<title>.png" or ".pdf". */
  const exportAs = async (kind: 'png' | 'pdf') => {
    try {
      const bytes = kind === 'png'
        ? await mapPNG({ content: current(), project, style })
        : await mapPDF({ content: current(), project, style, title });
      const name = exportFileName(title, kind);
      saveDownload(name, bytes, kind === 'png' ? 'image/png' : 'application/pdf');
      tell(`Exported “${name}”.`);
    } catch (err) {
      tell(message(err));
    }
  };

  // A new map opens with its centre selected and its words selected, ready to type over.
  useEffect(() => {
    if (!editable || !content.centre || !takeCentreEdit(fragmentId)) return;
    startEdit(content.centre, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fragmentId, content.centre !== null]);

  // MARK: Pointer

  const local = (e: { clientX: number; clientY: number }) => {
    const r = svgRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const paperAt = (e: { clientX: number; clientY: number }) => {
    const p = local(e);
    return toPaper(viewRef.current, p.x, p.y);
  };
  const noteAt = (target: EventTarget | null) => (target instanceof Element ? target.closest<SVGGElement>('[data-map-note]')?.dataset.mapNote ?? null : null);
  const lineAt = (target: EventTarget | null) => (target instanceof Element ? target.closest<SVGGElement>('[data-map-line]')?.dataset.mapLine ?? null : null);
  /** The region under a point on the paper: the one drawn last (on top) that holds it. */
  const regionAt = (p: MapPoint): MapRegion | null =>
    [...content.regions].reverse().find((r) => p.x >= r.at.x && p.x <= r.at.x + r.width && p.y >= r.at.y && p.y <= r.at.y + r.height) ?? null;
  const selectedRegions = selection.regions ?? [];
  const selectedStrokes = selection.strokes ?? [];

  const onPointerDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    const target = e.target as Element;
    if (target.closest('[data-map-note-editor]')) return;
    const touch = e.pointerType === 'touch';
    pointers.current.set(e.pointerId, { ...local(e), touch });
    const touches = [...pointers.current.values()].filter((p) => p.touch);
    if (touch && touches.length === 2) {
      // Two fingers: pinch to zoom (and pan), whatever the first finger was doing.
      const g = gesture.current;
      if (g?.kind === 'touch') clearTimeout(g.timer);
      setDrag(null);
      setMarquee(null);
      setInking(null);
      const [a, b] = touches;
      gesture.current = { kind: 'pinch', distance: Math.hypot(a.x - b.x, a.y - b.y), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, view: viewRef.current };
      return;
    }
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (pointers.current.size > 1) return;
    svgRef.current?.setPointerCapture?.(e.pointerId);
    // The pen: a pen always draws; a mouse or a finger with the Pen on. Over notes too, so a stroke can join them.
    if (editable && (pen.on || e.pointerType === 'pen')) {
      e.preventDefault();
      pointerFocus.current = true;
      if (editingRef.current) finishEdit('blur');
      wrapRef.current?.focus({ preventScroll: true });
      const p = paperAt(e);
      gesture.current = { kind: 'ink', pointerId: e.pointerId };
      setInking([{ x: p.x, y: p.y, pressure: pointerPressure(e) }]);
      return;
    }
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;
    const knob = target.closest('[data-map-knob]');
    const handle = target.closest<SVGElement>('[data-map-region-handle]')?.dataset.mapRegionHandle;
    const note = noteAt(target);
    const line = lineAt(target);
    if (knob && editable && selection.notes.length === 1) {
      e.preventDefault();
      gesture.current = { kind: 'connect', from: selection.notes[0], start: local(e) };
      setConnect({ from: selection.notes[0], to: paperAt(e) });
      return;
    }
    if (handle && editable) {
      const r = content.regions.find((x) => x.id === handle);
      if (r) {
        e.preventDefault();
        gesture.current = { kind: 'resize', id: r.id, start: local(e), width: r.width, height: r.height };
        return;
      }
    }
    if (note) {
      pointerFocus.current = true;
      let notes = selection.notes;
      // Chosen ink goes with the chosen notes; a note chosen afresh lets it go.
      const strokes = additive || notes.includes(note) ? (selection.strokes ?? []) : [];
      if (additive) notes = notes.includes(note) ? notes.filter((n) => n !== note) : [...notes, note];
      else if (!notes.includes(note)) notes = [note];
      setSelection({ notes, lines: additive ? selection.lines : [], strokes });
      const ids = e.altKey ? [...MapLayout.branch(note, content)] : notes.includes(note) ? notes : [];
      gesture.current = editable && ids.length ? { kind: 'move', id: note, ids, strokes: e.altKey ? [] : strokes, start: local(e), moved: false, additive } : null;
      return;
    }
    if (line) {
      setSelection(additive ? { notes: selection.notes, lines: selection.lines.includes(line) ? selection.lines.filter((l) => l !== line) : [...selection.lines, line] } : { notes: [], lines: [line] });
      gesture.current = null;
      wrapRef.current?.focus({ preventScroll: true });
      return;
    }
    // The paper.
    if (editingRef.current) finishEdit('blur');
    wrapRef.current?.focus({ preventScroll: true });
    if (touch) {
      const start = local(e);
      const timer = setTimeout(() => {
        const g = gesture.current;
        if (g?.kind !== 'touch') return;
        // A long press: a marquee from here.
        const a = toPaper(viewRef.current, start.x, start.y);
        gesture.current = { kind: 'marquee', a, base: [], baseStrokes: [] };
        setSelection(NOTHING);
        setMarquee({ a, b: a, base: [], baseStrokes: [] });
      }, 450);
      gesture.current = { kind: 'touch', start, view: viewRef.current, timer, marquee: false };
      return;
    }
    const a = paperAt(e);
    const stroke = MapInk.strokeAt(a, content.strokes, STROKE_REACH / viewRef.current.k);
    if (stroke) {
      // A stroke: chosen (Shift adds or takes it away); dragged, it moves with everything chosen.
      const chosen = selection.strokes ?? [];
      const next: Selection = additive
        ? { ...selection, regions: [], strokes: chosen.includes(stroke) ? chosen.filter((s) => s !== stroke) : [...chosen, stroke] }
        : chosen.includes(stroke) ? selection : { notes: [], lines: [], strokes: [stroke] };
      setSelection(next);
      const strokes = next.strokes ?? [];
      gesture.current = editable && strokes.includes(stroke)
        ? { kind: 'move', id: stroke, stroke: true, ids: next.notes, strokes, start: local(e), moved: false, additive }
        : null;
      return;
    }
    const region = additive ? null : regionAt(a);
    if (region && editable && selectedRegions.includes(region.id)) {
      // A chosen region dragged: it moves, with the notes lying in it.
      const stacked = new Set(heads.keys());
      const notes = MapArrange.notesInRegion(region, rest.frames).filter((id) => !stacked.has(id));
      gesture.current = { kind: 'region', id: region.id, notes, start: local(e), moved: false };
      return;
    }
    const base = additive ? selection.notes : [];
    const baseStrokes = additive ? (selection.strokes ?? []) : [];
    if (region) setSelection({ notes: [], lines: [], regions: [region.id] });
    else if (!additive) setSelection(NOTHING);
    gesture.current = { kind: 'marquee', a, base, baseStrokes };
    setMarquee({ a, b: a, base, baseStrokes });
  };

  const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    const p = pointers.current.get(e.pointerId);
    if (p) pointers.current.set(e.pointerId, { ...local(e), touch: p.touch });
    const g = gesture.current;
    if (!g) return;
    const here = local(e);
    if (g.kind === 'ink') {
      if (e.pointerId !== g.pointerId) return;
      const events = typeof e.nativeEvent.getCoalescedEvents === 'function' ? e.nativeEvent.getCoalescedEvents() : [];
      const samples = (events.length ? events : [e.nativeEvent]).map((ev) => {
        const p = paperAt(ev);
        return { x: p.x, y: p.y, pressure: pointerPressure(ev) };
      });
      setInking([...(inkingRef.current ?? []), ...samples]);
      return;
    }
    if (g.kind === 'pinch') {
      const touches = [...pointers.current.values()].filter((t) => t.touch);
      if (touches.length < 2) return;
      const [a, b] = touches;
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const moved = { ...g.view, x: g.view.x + mid.x - g.mid.x, y: g.view.y + mid.y - g.mid.y };
      setView(zoomAbout(moved, g.view.k * (distance / Math.max(g.distance, 1)), mid.x, mid.y));
    } else if (g.kind === 'move') {
      const dx = here.x - g.start.x, dy = here.y - g.start.y;
      if (!g.moved && Math.hypot(dx, dy) < 4) return;
      g.moved = true;
      setDrag({ ids: new Set(g.ids), dx: dx / viewRef.current.k, dy: dy / viewRef.current.k, strokes: new Set(g.strokes) });
    } else if (g.kind === 'connect') {
      setConnect({ from: g.from, to: paperAt(e) });
    } else if (g.kind === 'region') {
      const dx = here.x - g.start.x, dy = here.y - g.start.y;
      if (!g.moved && Math.hypot(dx, dy) < 4) return;
      g.moved = true;
      setDrag({ ids: new Set(g.notes), dx: dx / viewRef.current.k, dy: dy / viewRef.current.k, region: g.id });
    } else if (g.kind === 'resize') {
      const k = viewRef.current.k;
      setResize({ id: g.id, width: Math.max(REGION_MIN.width, g.width + (here.x - g.start.x) / k), height: Math.max(REGION_MIN.height, g.height + (here.y - g.start.y) / k) });
    } else if (g.kind === 'marquee') {
      const b = paperAt(e);
      setMarquee({ a: g.a, b, base: g.base, baseStrokes: g.baseStrokes });
    } else if (g.kind === 'touch') {
      if (Math.hypot(here.x - g.start.x, here.y - g.start.y) < 6) return;
      clearTimeout(g.timer);
      gesture.current = { kind: 'pan', start: g.start, view: g.view };
      setView({ ...g.view, x: g.view.x + here.x - g.start.x, y: g.view.y + here.y - g.start.y });
    } else if (g.kind === 'pan') {
      setView({ ...g.view, x: g.view.x + here.x - g.start.x, y: g.view.y + here.y - g.start.y });
    }
  };

  const onPointerUp = (e: ReactPointerEvent<SVGSVGElement>) => {
    pointers.current.delete(e.pointerId);
    svgRef.current?.releasePointerCapture?.(e.pointerId);
    pointerFocus.current = false;
    const g = gesture.current;
    if (g?.kind === 'pinch') {
      if (pointers.current.size === 0) gesture.current = null;
      return;
    }
    gesture.current = null;
    if (!g) return;
    if (g.kind === 'ink') {
      const drawn = inkingRef.current ?? [];
      setInking(null);
      if (drawn.length) finishStroke(drawn);
      return;
    }
    if (g.kind === 'move') {
      if (!g.moved) {
        // A click on one of several selected notes (or strokes) chooses it alone.
        const several = selection.notes.length + (selection.strokes?.length ?? 0) > 1;
        if (!g.additive && several && !e.altKey) setSelection(g.stroke ? { notes: [], lines: [], strokes: [g.id] } : { notes: [g.id], lines: [] });
        setDrag(null);
        return;
      }
      const k = viewRef.current.k;
      const here = local(e);
      const dx = (here.x - g.start.x) / k, dy = (here.y - g.start.y) / k;
      // A note in a stack dragged without its head leaves the stack; one dragged with its head goes with it.
      const moving = new Set(g.ids);
      const loose = g.ids.filter((id) => heads.has(id) && !moving.has(heads.get(id)!));
      const places = new Map(g.ids.flatMap((id) => {
        if (heads.has(id) && !loose.includes(id)) return [];
        const f = rest.frames.get(id);
        return f ? [[id, { x: f.x + dx, y: f.y + dy }] as const] : [];
      }));
      const offsets = strokeOffsets(current().strokes, g.strokes, dx, dy);
      apply((m) => {
        for (const id of loose) m.setMapNote(id, { stack: null, order: null });
        m.moveMapNotes(places);
        m.moveMapStrokes(offsets);
      });
      setDrag(null);
    } else if (g.kind === 'region') {
      setDrag(null);
      if (!g.moved) return;
      const k = viewRef.current.k;
      const here = local(e);
      const dx = (here.x - g.start.x) / k, dy = (here.y - g.start.y) / k;
      const region = content.regions.find((r) => r.id === g.id);
      if (!region) return;
      const places = new Map(g.notes.flatMap((id) => { const f = rest.frames.get(id); return f ? [[id, { x: f.x + dx, y: f.y + dy }] as const] : []; }));
      apply((m) => {
        m.updateMapRegion({ ...region, at: { x: region.at.x + dx, y: region.at.y + dy } });
        m.moveMapNotes(places);
      });
    } else if (g.kind === 'resize') {
      const sized = resize;
      setResize(null);
      const region = content.regions.find((r) => r.id === g.id);
      if (region && sized && (sized.width !== region.width || sized.height !== region.height)) apply((m) => m.updateMapRegion({ ...region, width: sized.width, height: sized.height }));
    } else if (g.kind === 'connect') {
      setConnect(null);
      const over = noteAt(document.elementFromPoint(e.clientX, e.clientY));
      if (over && over !== g.from) {
        if (apply((m) => m.connectMapNotes(g.from, over))) announce('Joined');
      } else if (!over && Math.hypot(local(e).x - g.start.x, local(e).y - g.start.y) > 12) {
        const p = paperAt(e);
        createNote({ x: p.x - 6, y: p.y - NEW_SIZE.height / 2 }, g.from);
      }
    } else if (g.kind === 'marquee') {
      setMarquee(null);
      const b = paperAt(e);
      if (Math.hypot(b.x - g.a.x, b.y - g.a.y) * viewRef.current.k < 3) return;
      const inside = inMarquee(frames, g.a, b);
      const strokes = [...new Set([...g.baseStrokes, ...strokesInMarquee(content.strokes, g.a, b)])];
      setSelection({ notes: [...new Set([...g.base, ...inside])], lines: [], ...(strokes.length ? { strokes } : {}) });
    } else if (g.kind === 'touch') {
      clearTimeout(g.timer);
      const at = toPaper(viewRef.current, g.start.x, g.start.y);
      const stroke = MapInk.strokeAt(at, content.strokes, STROKE_REACH / viewRef.current.k);
      const region = stroke ? null : regionAt(at);
      setSelection(stroke ? { notes: [], lines: [], strokes: [stroke] } : region ? { notes: [], lines: [], regions: [region.id] } : NOTHING);
    }
  };

  const onPointerCancel = (e: ReactPointerEvent<SVGSVGElement>) => {
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (g?.kind === 'touch') clearTimeout(g.timer);
    gesture.current = null;
    setDrag(null);
    setResize(null);
    setMarquee(null);
    setConnect(null);
    setInking(null);
  };

  /** A card opens its document (in Write, or Research for a note, as the contents open it); a chip its character's study
   *  page when they have one, else Cast & Places. */
  const openLink = (note: MapNote) => {
    if (!note.link || !linkLives(project, note.link)) return;
    if (note.link.kind === 'fragment') {
      ws?.reveal({ fragmentId: note.link.id });
      return;
    }
    const entity = castOf(project).find((x) => x.id === note.link!.id);
    navigate(hrefFor(entity?.kind === 'character' && entity.studyID ? 'plan/study' : 'plan/cast', null, { who: note.link.id }));
  };

  // Documents dragged from the contents (or the corkboard): a card each at the drop, 30 points apart down.
  const onDragOver = (e: React.DragEvent) => {
    if (!editable || !e.dataTransfer.types.includes(FRAGMENTS_TYPE)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onDrop = (e: React.DragEvent) => {
    if (!editable || !e.dataTransfer.types.includes(FRAGMENTS_TYPE)) return;
    e.preventDefault();
    let ids: string[] = [];
    try {
      ids = (JSON.parse(e.dataTransfer.getData(FRAGMENTS_TYPE) || '[]') as unknown[]).filter((x): x is string => typeof x === 'string');
    } catch {
      return;
    }
    const choices = ids.flatMap((id): MapChoice[] => {
      const f = project.fragment(id);
      return f && id !== fragmentId ? [{ kind: 'fragment', id, title: f.title.trim() || 'Untitled', place: '' }] : [];
    });
    if (!choices.length) return;
    const p = paperAt(e);
    addLinked(choices, dropPlaces({ x: p.x - 6, y: p.y - NEW_SIZE.height / 2 }, choices.length));
    wrapRef.current?.focus({ preventScroll: true });
  };

  const onDoubleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if ((e.target as Element).closest('[data-map-note-editor], [data-map-knob]')) return;
    // With the Pen on, a double click is two dots of ink.
    if (editable && pen.on) return;
    const id = noteAt(e.target);
    if (id) {
      const note = byID.get(id);
      if (note && linkKind(note)) openLink(note);
      else startEdit(id);
      return;
    }
    if (lineAt(e.target) || !editable) return;
    const p = paperAt(e);
    createNote({ x: p.x - 6, y: p.y - NEW_SIZE.height / 2 }, null);
  };

  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    const note = noteAt(e.target), line = lineAt(e.target);
    const region = !note && !line ? regionAt(paperAt(e)) : null;
    if (note && !selection.notes.includes(note)) setSelection({ notes: [note], lines: [] });
    else if (line && !selection.lines.includes(line)) setSelection({ notes: [], lines: [line] });
    else if (region) setSelection({ notes: [], lines: [], regions: [region.id] });
    else if (!note && !line) setSelection(NOTHING);
    setMenu({ x: e.clientX, y: e.clientY });
  };

  // MARK: Keys

  const primary = selection.notes.at(-1) ?? null;
  const stepTo = (id: string | null) => {
    if (id) select({ notes: [id], lines: [] }, true);
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const target = e.target as Element;
    if (target.closest('[data-map-note-editor], .map-label-input')) return;
    if (target.closest('.map-style-bar') && e.key !== 'Escape') return;
    const mod = e.metaKey || e.ctrlKey;
    const key = e.key;
    const handled = () => e.preventDefault();
    if (mod && !e.altKey && key.toLowerCase() === 'z') {
      handled();
      undo(e.shiftKey);
    } else if (mod && !e.altKey && key === '0') {
      handled();
      fit();
    } else if (mod && !e.altKey && (key === '=' || key === '+')) {
      handled();
      zoomBy(1.25);
    } else if (mod && !e.altKey && key === '-') {
      handled();
      zoomBy(1 / 1.25);
    } else if (mod && !e.altKey && key.toLowerCase() === 'a') {
      handled();
      setSelection({ notes: content.notes.map((n) => n.id), lines: [] });
    } else if (key === 'Escape') {
      if (selection.notes.length || selection.lines.length || selectedRegions.length || selectedStrokes.length) {
        handled();
        setSelection(NOTHING);
        wrapRef.current?.focus({ preventScroll: true });
      }
    } else if (key === 'Tab' && !e.shiftKey && !mod && !e.altKey && primary && editable) {
      handled();
      makeChild(primary);
    } else if (key === 'Enter' && !mod && !e.altKey && primary && editable) {
      handled();
      if (e.shiftKey) startEdit(primary);
      else makeSibling(primary);
    } else if ((key === ' ' || key === 'F2') && primary && editable && !mod) {
      handled();
      const note = byID.get(primary);
      if (note && linkKind(note)) openLink(note);
      else startEdit(primary);
    } else if ((key === 'Backspace' || key === 'Delete') && editable && (selection.notes.length || selection.lines.length || selectedRegions.length || selectedStrokes.length)) {
      handled();
      removeSelection();
    } else if (key.startsWith('Arrow') && !mod) {
      const direction = key.slice(5).toLowerCase() as MapDirection;
      handled();
      if (e.altKey && primary) stepTo(MapLayout.step(primary, direction, content, frames));
      else if (!e.altKey) {
        // Through the notes in reading order.
        const i = primary ? order.indexOf(primary) : -1;
        const next = direction === 'up' || direction === 'left' ? (i <= 0 ? order.length - 1 : i - 1) : (i + 1) % Math.max(order.length, 1);
        stepTo(order[next] ?? null);
      }
    } else if (key === 'ContextMenu' || (key === 'F10' && e.shiftKey)) {
      handled();
      const f = primary ? frames.get(primary) : null;
      const r = svgRef.current!.getBoundingClientRect();
      const v = viewRef.current;
      setMenu(f ? { x: r.left + (f.x + f.width) * v.k + v.x, y: r.top + f.y * v.k + v.y } : { x: r.left + 40, y: r.top + 40 });
    }
  };

  // MARK: What the style bar and the menu offer

  const selectedNotes = selection.notes.flatMap((id) => byID.get(id) ?? []);
  const oneLine = selection.lines.length === 1 && !selection.notes.length ? content.lines.find((l) => l.id === selection.lines[0]) ?? null : null;
  const oneRegion = selectedRegions.length === 1 && !selection.notes.length && !selection.lines.length ? drawn.regions.find((r) => r.id === selectedRegions[0]) ?? null : null;
  const allBold = selectedNotes.length > 0 && selectedNotes.every((n) => n.bold);
  /** Strokes chosen with nothing else: the bar and the menu offer their ink and Delete. */
  const strokesAlone = selectedStrokes.length && !selection.notes.length && !selection.lines.length && !selectedRegions.length
    ? content.strokes.filter((s) => selectedStrokes.includes(s.id))
    : null;
  const strokeInk = strokesAlone?.length && strokesAlone.every((s) => s.ink === strokesAlone[0].ink) ? strokesAlone[0].ink : undefined;
  const same = <T,>(get: (n: MapNote) => T): T | undefined => (selectedNotes.length && selectedNotes.every((n) => get(n) === get(selectedNotes[0])) ? get(selectedNotes[0]) : undefined);
  const inkEntries = (current: InkColor | null | undefined, set: (ink: InkColor | null) => void): MenuEntry[] => [
    { kind: 'radio', label: 'Charcoal', checked: current === null, color: 'var(--map-ink)', onSelect: () => set(null) },
    ...INKS.map((i): MenuEntry => ({ kind: 'radio', label: i.name, checked: current === i.ink, color: `var(--ink-${i.ink})`, onSelect: () => set(i.ink) })),
  ];
  const noteInk = same((n) => n.ink);
  const noteOutline = same((n) => n.outline);
  const noteSize = same((n) => n.size);
  const noteFace = same((n) => n.face);
  const centreEntry = (): MenuEntry | null => {
    if (selectedNotes.length !== 1) return null;
    const id = selectedNotes[0].id;
    return content.centre === id ? { label: 'Clear Centre', onSelect: clearCentre } : { label: 'Make This the Centre', onSelect: () => makeCentre(id) };
  };

  /** What the chosen notes can do with the project and the map's shape: Link…/Unlink, Select Branch, Tidy Branch,
   *  Stack/Unstack, Make Region, Turn Into Scenes… — for the context menu and the style bar's More menu. */
  const projectEntries = (withLink = true, withRegion = true): MenuEntry[] => {
    if (!selectedNotes.length) return [];
    const single = selectedNotes.length === 1 ? selectedNotes[0] : null;
    const ids = selectedNotes.map((n) => n.id);
    const eligible = MapScenes.eligible(content, new Set(ids));
    const linked = single ? linkKind(single) !== null || single.link !== null : false;
    const linkEntries: MenuEntry[] = single && withLink
      ? [linked
        ? { label: 'Unlink', disabled: !editable, onSelect: () => unlinkNote(single.id) }
        : { label: 'Link…', disabled: !editable, onSelect: () => setDialog({ kind: 'link', note: single.id }) }]
      : [];
    const branchEntries: MenuEntry[] = single
      ? [
          { label: 'Select Branch', onSelect: () => selectBranch(single.id) },
          ...(wouldMove(MapArrange.tidyBranch(single.id, asDrawn(content), rest.frames)) ? [{ label: 'Tidy Branch', disabled: !editable, onSelect: () => tidyBranch(single.id) }] : []),
        ]
      : [];
    const inStack = single && (heads.has(single.id) || stacksNow.has(single.id));
    const stackEntries: MenuEntry[] = inStack
      ? [{ label: 'Unstack', disabled: !editable, onSelect: () => unstackNote(single!.id) }]
      : ids.length > 1 ? [{ label: 'Stack', disabled: !editable, onSelect: () => stackNotes(ids) }] : [];
    return [
      ...linkEntries,
      ...(linkEntries.length ? [{ kind: 'separator' as const }] : []),
      ...branchEntries,
      ...stackEntries,
      ...(withRegion ? [{ label: 'Make Region', disabled: !editable, onSelect: () => makeRegion(ids) }] : []),
      ...(eligible.size ? [{ label: 'Turn Into Scenes…', disabled: !editable, onSelect: () => setDialog({ kind: 'scenes', notes: ids }) }] : []),
    ];
  };

  const menuEntries = (): MenuEntry[] => {
    if (selectedNotes.length) {
      const single = selectedNotes.length === 1 ? selectedNotes[0] : null;
      const centre = centreEntry();
      const live = single ? linkKind(single) : null;
      return [
        ...(single && !live ? [{ label: 'Edit Note', shortcut: 'Space', disabled: !editable, onSelect: () => startEdit(single.id) }] : []),
        ...(single && live ? [{ label: live === 'fragment' ? 'Open' : 'Show in Cast & Places', onSelect: () => openLink(single) }] : []),
        ...(single ? [
          { label: 'New Joined Note', shortcut: 'Tab', disabled: !editable, onSelect: () => makeChild(single.id) },
          { label: 'New Note Beside It', shortcut: 'Return', disabled: !editable, onSelect: () => makeSibling(single.id) },
        ] : []),
        { kind: 'separator' as const },
        ...projectEntries(),
        ...(editable ? [
          ...(centre ? [{ kind: 'separator' as const }, centre] : []),
          { kind: 'separator' as const },
          { kind: 'section' as const, label: 'Ink', entries: inkEntries(noteInk, (ink) => setNotes((m, id) => m.setMapNote(id, { ink }))) },
          { kind: 'section' as const, label: 'Outline', entries: OUTLINES.map((o): MenuEntry => ({ kind: 'radio', label: o.name, checked: noteOutline === o.value, onSelect: () => setNotes((m, id) => m.setMapNote(id, { outline: o.value })) })) },
          { kind: 'section' as const, label: 'Size', entries: SIZES.map((s): MenuEntry => ({ kind: 'radio', label: s.name, checked: noteSize === s.value, onSelect: () => setNotes((m, id) => m.setMapNote(id, { size: s.value })) })) },
          { kind: 'check' as const, label: 'Bold', checked: allBold, onSelect: () => setNotes((m, id) => m.setMapNote(id, { bold: !allBold })) },
          { kind: 'section' as const, label: 'Face', entries: [
            { kind: 'radio' as const, label: 'Serif', checked: noteFace === 'serif', onSelect: () => chooseFace('serif') },
            { kind: 'radio' as const, label: 'Hand', checked: noteFace === 'hand', onSelect: () => chooseFace('hand') },
          ] },
          { kind: 'separator' as const },
          { label: selectedNotes.length === 1 ? 'Delete Note' : 'Delete Notes', shortcut: '⌫', danger: true, onSelect: removeSelection },
        ] : []),
      ];
    }
    if (selection.lines.length) {
      const line = oneLine;
      return editable ? [
        ...(line ? [
          { kind: 'section' as const, label: 'Arrows', entries: ARROWS.map((a): MenuEntry => ({ kind: 'radio', label: a.name, checked: line.arrow === a.value, onSelect: () => setLines((m, id) => m.setMapLine(id, { arrow: a.value })) })) },
          { kind: 'check' as const, label: 'Dotted', checked: line.style === 'dotted', onSelect: () => setLines((m, id) => m.setMapLine(id, { style: line.style === 'dotted' ? 'solid' : 'dotted' })) },
          { kind: 'section' as const, label: 'Ink', entries: inkEntries(line.ink, (ink) => setLines((m, id) => m.setMapLine(id, { ink }))) },
          { label: line.label ? 'Edit Label…' : 'Label…', onSelect: () => setLabeling(line.id) },
          { kind: 'separator' as const },
        ] : []),
        { label: selection.lines.length === 1 ? 'Delete Line' : 'Delete Lines', shortcut: '⌫', danger: true, onSelect: removeSelection },
      ] : [];
    }
    if (strokesAlone?.length) {
      return editable ? [
        { kind: 'section' as const, label: 'Ink', entries: inkEntries(strokeInk, setStrokesInk) },
        { kind: 'separator' as const },
        { label: strokesAlone.length === 1 ? 'Delete Stroke' : 'Delete Strokes', shortcut: '⌫', danger: true, onSelect: removeSelection },
      ] : [];
    }
    if (oneRegion) {
      const region = oneRegion;
      return editable ? [
        { kind: 'section' as const, label: 'Ink', entries: inkEntries(region.ink, (ink) => setRegions((r) => ({ ...r, ink }))) },
        { label: region.label ? 'Edit Label…' : 'Label…', onSelect: () => setLabeling(region.id) },
        { kind: 'separator' as const },
        { label: 'Delete Region', shortcut: '⌫', danger: true, onSelect: removeSelection },
      ] : [];
    }
    const at = menu ? paperAt({ clientX: menu.x, clientY: menu.y }) : null;
    return [
      ...(editable && at ? [
        { label: 'New Note Here', onSelect: () => createNote({ x: at.x - 6, y: at.y - NEW_SIZE.height / 2 }, null) },
        { label: 'Add to Map…', onSelect: () => setDialog({ kind: 'add', at: { x: at.x - 6, y: at.y - NEW_SIZE.height / 2 } }) },
        { kind: 'separator' as const },
        { label: 'Link Notes to Cast…', disabled: !content.notes.length, onSelect: () => setDialog({ kind: 'cast' }) },
        { label: 'Arrange Around Centre', disabled: !content.centre, onSelect: arrangeAroundCentre },
        { kind: 'separator' as const },
      ] : []),
      { label: 'Select All', shortcut: '⌘A', disabled: !content.notes.length, onSelect: () => setSelection({ notes: content.notes.map((n) => n.id), lines: [] }) },
      { label: 'Zoom to Fit', shortcut: '⌘0', onSelect: () => fit() },
      { kind: 'separator' as const },
      { label: 'Export as PNG…', onSelect: () => void exportAs('png') },
      { label: 'Export as PDF…', onSelect: () => void exportAs('pdf') },
      ...(editable ? [{ kind: 'separator' as const }, { label: 'Undo', shortcut: '⌘Z', disabled: !history.current.canUndo, onSelect: () => undo() }, { label: 'Redo', shortcut: '⇧⌘Z', disabled: !history.current.canRedo, onSelect: () => undo(true) }] : []),
    ];
  };

  // MARK: Drawing

  const far = view.k < FAR;
  const near = view.k > NEAR;
  const marks = (list: SketchMark[], className = 'map-mark') =>
    list.map((m, i) => <path key={i} className={className} d={markPath(m)} fillOpacity={m.opacity} />);

  const lineShapes: ReactNode[] = [];
  const lineLabels: ReactNode[] = [];
  const drawnStacks = MapArrange.stacks(drawn);
  for (const line of drawn.lines) {
    // A line between notes of one stack isn't drawn (nor its label, nor can it be chosen): the stack shows they belong.
    if (MapArrange.isWithinStack(line, drawnStacks)) continue;
    const ends = MapLayout.lineEnds(line, forEnds, frames);
    if (!ends) continue;
    const [a, b] = ends;
    const passes = MapSketch.line(a, b, line.id, style);
    if (!passes.length) continue;
    const spine = passes[0].points;
    const arrows: SketchMark[] = [];
    if (line.arrow !== 'none') arrows.push(...MapSketch.arrowhead(b, spine[Math.max(0, spine.length - 3)], line.id, style));
    if (line.arrow === 'both') arrows.push(...MapSketch.arrowhead(a, spine[Math.min(spine.length - 1, 2)], `${line.id}|start`, style));
    const selected = selection.lines.includes(line.id);
    lineShapes.push(
      <g key={line.id} data-map-line={line.id} className={`map-line${inkClass(line.ink)}${selected ? ' selected' : ''}`}>
        <polyline className="map-line-hit" points={polylinePoints(spine)} />
        {line.style === 'dotted' ? <polyline className="map-line-dots" points={polylinePoints(spine)} /> : marks(passes)}
        {marks(arrows)}
      </g>,
    );
    const mid = midPoint(spine);
    if (line.label.trim() && mid) {
      lineLabels.push(
        <text key={line.id} data-map-line={line.id} className={`map-line-label${inkClass(line.ink)}${selected ? ' selected' : ''}`} x={mid.x} y={mid.y + 5} textAnchor="middle">
          {line.label.trim()}
        </text>,
      );
    }
  }

  const outlines: ReactNode[] = [];
  const highlights: ReactNode[] = [];
  const edges: ReactNode[] = [];
  const cards = new Map<string, CardInfo>();
  for (const n of drawn.notes) {
    if (n.link?.kind !== 'fragment' || linkKind(n) !== 'fragment') continue;
    const info = cardInfo(project, n.link.id, chapters);
    if (info) cards.set(n.id, info);
  }
  // A stack is marked by a pencil line down its left.
  for (const [head, members] of drawnStacks) {
    const top = frames.get(head), last = frames.get(members[members.length - 1]);
    if (!top || !last) continue;
    const [a, b] = stackLine(top, last);
    outlines.push(<g key={`stack:${head}`} className="map-stack-line" data-map-stack={head}>{marks(MapSketch.line(a, b, `${head}|stack`, style))}</g>);
  }
  for (const n of drawn.notes) {
    const f = frames.get(n.id)!;
    const edge = cards.get(n.id)?.edge;
    // A card's strip in the POV's (else the label's) colour, along the top inside its box.
    if (edge) edges.push(<rect key={n.id} className={`map-card-edge${cards.get(n.id)?.trashed ? ' trashed' : ''}`} x={f.x - 6} y={f.y - 4} width={f.width + 12} height={3} rx={1} fill={edge} />);
    const shape = outlineOf(n);
    if (shape !== 'none') {
      outlines.push(
        <g key={n.id} className={`map-outline${inkClass(n.ink)}`}>
          {marks(MapSketch.outline(shape, f.x, f.y, f.width, f.height, n.id, style))}
        </g>,
      );
    }
    if (selection.notes.includes(n.id) || marquee) {
      const inside = marquee ? inMarquee(new Map([[n.id, f]]), marquee.a, marquee.b).length > 0 || marquee.base.includes(n.id) : false;
      if (selection.notes.includes(n.id) || inside) {
        highlights.push(<rect key={n.id} className="map-selection" x={f.x - 7} y={f.y - 5} width={f.width + 14} height={f.height + 10} rx={8} />);
      }
    }
  }

  // Ink: each stroke where it's been moved to (and where a drag has it), as wide as the pen pressed; a chosen one (or one
  // a marquee crosses) with a faint terracotta halo under it.
  const strokeShapes: ReactNode[] = [];
  const strokeHalos: ReactNode[] = [];
  const marqueeStrokes = marquee ? new Set([...marquee.baseStrokes, ...strokesInMarquee(content.strokes, marquee.a, marquee.b)]) : null;
  for (const s of content.strokes) {
    const moving = drag?.strokes?.has(s.id);
    const offset = `${s.offset.x} ${s.offset.y}`;
    const points = strokeMarkPoints(placedStroke(s), s.width, moving ? drag!.dx : 0, moving ? drag!.dy : 0);
    if (selectedStrokes.includes(s.id) || marqueeStrokes?.has(s.id)) {
      strokeHalos.push(<polyline key={s.id} className="map-stroke-halo" points={polylinePoints(points.length === 1 ? [points[0], points[0]] : points)} />);
    }
    strokeShapes.push(points.length === 1
      ? <circle key={s.id} className={`map-mark map-stroke${inkClass(s.ink)}`} data-map-stroke={s.id} data-offset={offset} cx={points[0].x} cy={points[0].y} r={points[0].width / 2} fillOpacity={0.9} />
      : <path key={s.id} className={`map-mark map-stroke${inkClass(s.ink)}`} data-map-stroke={s.id} data-offset={offset} d={markPath({ kind: 'stroke', opacity: 0.9, points })} fillOpacity={0.9} />);
  }
  const inkingShape = inking && inking.length > 1
    ? <path className={`map-mark map-stroke map-inking${inkClass(pen.ink)}`} d={markPath({ kind: 'stroke', opacity: 0.9, points: strokeMarkPoints(inking, PEN_WIDTH) })} fillOpacity={0.9} />
    : null;

  const knob = editable && selection.notes.length === 1 && !editing && !drag && !inking ? frames.get(selection.notes[0]) : null;
  const provisional = (() => {
    if (!connect) return null;
    const from = byID.get(connect.from), f = frames.get(connect.from);
    if (!from || !f) return null;
    const a = edgeToward(f, from, connect.to.x, connect.to.y);
    return marks(MapSketch.line(a, connect.to, `${connect.from}|joining`, style), 'map-mark map-provisional');
  })();

  const rovingID = primary && byID.has(primary) ? primary : (order[0] ?? null);
  const descID = `map-keys-${fragmentId}`;
  const titleID = `map-title-${fragmentId}`;

  // The style bar sits above the selection (below it when that's off the top).
  const barFrame = !editing && !drag && !marquee && !inking && editable
    ? selection.notes.length
      ? unionFrame(selection.notes.flatMap((id) => frames.get(id) ?? []))
      : oneLine
        ? (() => {
            const ends = MapLayout.lineEnds(oneLine, forEnds, frames);
            return ends ? { x: Math.min(ends[0].x, ends[1].x), y: Math.min(ends[0].y, ends[1].y), width: Math.abs(ends[1].x - ends[0].x), height: Math.abs(ends[1].y - ends[0].y) } : null;
          })()
        : oneRegion
          ? { x: oneRegion.at.x, y: oneRegion.at.y, width: oneRegion.width, height: oneRegion.height }
          : strokesAlone?.length
            ? MapInk.bounds(strokesAlone)
            : null
    : null;

  return (
    <div
      ref={wrapRef}
      className={`map-canvas${far ? ' far' : ''}${near ? ' near' : ''}${editable ? '' : ' read-only'}${editable && pen.on ? ' pen' : ''}`}
      tabIndex={rovingID ? -1 : 0}
      onKeyDown={onKeyDown}
      data-map={fragmentId}
      data-zoom={Math.round(view.k * 100)}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <svg
        ref={svgRef}
        className="map-svg"
        role="group"
        aria-labelledby={titleID}
        aria-describedby={descID}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onDoubleClick={onDoubleClick}
        onContextMenu={onContextMenu}
      >
        <title id={titleID}>{`Mind map: ${title}`}</title>
        <defs>
          <filter id="map-grain" x="-5%" y="-5%" width="110%" height="110%" colorInterpolationFilters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="3" result="noise" />
            <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -2.2 1.9" result="grain" />
            <feComposite in="SourceGraphic" in2="grain" operator="in" />
          </filter>
          <filter id="map-chalk" x="-5%" y="-5%" width="110%" height="110%" colorInterpolationFilters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.6" numOctaves="2" seed="5" result="noise" />
            <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -3 2.3" result="grain" />
            <feComposite in="SourceGraphic" in2="grain" operator="in" />
          </filter>
          <filter id="map-paper-grain" x="0" y="0" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" seed="11" result="noise" />
            <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1.6 -0.7" result="grain" />
            <feComposite in="SourceGraphic" in2="grain" operator="in" />
          </filter>
          <filter id="map-smudge" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="7" />
          </filter>
        </defs>
        <rect className="map-paper" width="100%" height="100%" />
        <rect className="map-paper-grain" width="100%" height="100%" filter="url(#map-paper-grain)" />
        <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
          <g className="map-regions">
            {drawn.regions.map((r) => (
              <g key={r.id} className={`map-region${inkClass(r.ink)}${selectedRegions.includes(r.id) ? ' selected' : ''}`} data-map-region={r.id} data-x={r.at.x} data-y={r.at.y} data-width={r.width} data-height={r.height}>
                <path className="map-region-wash" d={markPath(MapSketch.region(r.at.x, r.at.y, r.width, r.height, r.id, style))} filter="url(#map-smudge)" />
                {r.label.trim() ? <text className="map-region-label" x={r.at.x + 16} y={r.at.y + 14} dominantBaseline="hanging">{r.label.trim()}</text> : null}
              </g>
            ))}
          </g>
          <g className="map-highlights">
            {drawn.regions.filter((r) => selectedRegions.includes(r.id)).map((r) => (
              <rect key={r.id} className="map-region-selection" x={r.at.x} y={r.at.y} width={r.width} height={r.height} rx={10} />
            ))}
            {highlights}
            {strokeHalos}
          </g>
          <g className="map-ink">
            {lineShapes}
            {outlines}
            {strokeShapes}
            {inkingShape}
            {provisional}
          </g>
          <g className="map-card-edges">{edges}</g>
          <g className="map-labels">{lineLabels}</g>
          <g className="map-notes">
            {order.map((id) => {
              const n = byID.get(id);
              const f = frames.get(id);
              if (!n || !f) return null;
              const selected = selection.notes.includes(id);
              const isEditing = editing?.id === id;
              const width = n.width ?? MAP_AUTO_WIDTH;
              const measured = sizes.get(id);
              return (
                <g key={id} data-map-note={id} data-x={n.at.x} data-y={n.at.y} className={`map-note-g${inkClass(n.ink)}${selected ? ' selected' : ''}`}>
                  <foreignObject x={f.x} y={f.y} width={Math.max(width, f.width) + 24} height={(measured?.h ?? 400) + 4} className="map-note-fo">
                    {isEditing ? (
                      <NoteEditor
                        key={editing.key}
                        id={id}
                        initial={n.text}
                        selectAll={editing.selectAll}
                        className={noteClass(n, true, null)}
                        fixedWidth={n.width}
                        register={register(id)}
                        onInput={onTyping}
                        onEnd={finishEdit}
                      />
                    ) : (
                      <NoteFace
                        project={project}
                        note={n}
                        near={near}
                        far={far}
                        card={cards.get(id) ?? null}
                        live={linkKind(n)}
                        className={noteClass(n, false, linkKind(n))}
                        register={register(id)}
                        tabIndex={id === rovingID ? 0 : -1}
                        selected={selected}
                        label={noteLabel(content, id, words) + (linkKind(n) === 'fragment' ? (cards.get(id)?.trashed ? ', a document in the Trash' : ', a document') : linkKind(n) ? ', in the cast' : '')}
                        onFocus={() => {
                          if (!pointerFocus.current && !selection.notes.includes(id)) setSelection({ notes: [id], lines: [] });
                        }}
                      />
                    )}
                  </foreignObject>
                </g>
              );
            })}
          </g>
          {knob ? (
            <g className="map-knob" data-map-knob="" aria-hidden="true">
              <circle cx={knob.x + knob.width + 14} cy={knob.y + knob.height / 2} r={12 / view.k} className="map-knob-hit" />
              <circle cx={knob.x + knob.width + 14} cy={knob.y + knob.height / 2} r={5 / view.k} className="map-knob-dot" />
            </g>
          ) : null}
          {oneRegion && editable && !drag ? (
            <rect
              className="map-region-handle"
              data-map-region-handle={oneRegion.id}
              aria-hidden="true"
              x={oneRegion.at.x + oneRegion.width - 7 / view.k}
              y={oneRegion.at.y + oneRegion.height - 7 / view.k}
              width={14 / view.k}
              height={14 / view.k}
              rx={3 / view.k}
            />
          ) : null}
          {marquee ? (
            <rect
              className="map-marquee"
              x={Math.min(marquee.a.x, marquee.b.x)}
              y={Math.min(marquee.a.y, marquee.b.y)}
              width={Math.abs(marquee.b.x - marquee.a.x)}
              height={Math.abs(marquee.b.y - marquee.a.y)}
            />
          ) : null}
        </g>
      </svg>
      <p id={descID} className="visually-hidden">
        {editable
          ? 'Arrow keys go from note to note; Option or Alt with an arrow goes to the joined note that way. Tab makes a joined note, Return a note beside it, Space edits the note, Delete removes it, Escape clears the selection.'
          : 'Arrow keys go from note to note. This map is read only.'}
      </p>
      {!content.notes.length ? (
        <p className="map-empty-hint">{editable ? 'Double-click the paper for a note.' : 'Nothing on this map yet.'}</p>
      ) : null}
      {barFrame && (selectedNotes.length || oneLine || oneRegion || strokesAlone?.length) ? (
        <StyleBar
          at={{ x: barFrame.x * view.k + view.x, y: barFrame.y * view.k + view.y, bottom: (barFrame.y + barFrame.height) * view.k + view.y, width: box.width, height: box.height }}
          avoid={[...frames].filter(([id]) => !selection.notes.includes(id)).map(([, f]) => ({ x: f.x * view.k + view.x - 8, y: f.y * view.k + view.y - 8, width: f.width * view.k + 16, height: f.height * view.k + 16 }))}
          notes={selectedNotes}
          line={oneLine}
          region={oneRegion}
          strokes={strokesAlone?.length ? { ink: strokeInk, count: strokesAlone.length } : null}
          setStrokesInk={setStrokesInk}
          makeRegion={selectedNotes.length ? () => makeRegion(selectedNotes.map((n) => n.id)) : null}
          labeling={labeling !== null && (labeling === oneLine?.id || labeling === oneRegion?.id)}
          link={selectedNotes.length === 1 ? (selectedNotes[0].link ? { label: 'Unlink', onSelect: () => unlinkNote(selectedNotes[0].id) } : { label: 'Link…', onSelect: () => setDialog({ kind: 'link', note: selectedNotes[0].id }) }) : null}
          more={projectEntries(false, false)}
          setRegions={setRegions}
          removeRegion={removeSelection}
          inkEntries={inkEntries}
          ink={noteInk}
          outline={noteOutline}
          size={noteSize}
          face={noteFace}
          bold={allBold}
          centre={centreEntry()}
          setNotes={setNotes}
          setLines={setLines}
          chooseFace={chooseFace}
          startLabel={() => (oneLine ?? oneRegion) && setLabeling((oneLine ?? oneRegion)!.id)}
          endLabel={(text) => {
            const id = labeling;
            setLabeling(null);
            if (id !== null && text !== null) {
              const region = content.regions.find((r) => r.id === id);
              if (region) apply((m) => m.updateMapRegion({ ...region, label: text.trim() }));
              else apply((m) => m.setMapLine(id, { label: text.trim() }));
            }
            wrapRef.current?.focus({ preventScroll: true });
          }}
        />
      ) : null}
      {phone ? (
        <div className="map-phone-bar" role="toolbar" aria-label="Map">
          <button type="button" className="quiet-button" disabled={!editable || !primary} onMouseDown={(e) => e.preventDefault()} onClick={() => primary && makeChild(primary)}>
            Child
          </button>
          <button type="button" className="quiet-button" disabled={!editable || !primary} onMouseDown={(e) => e.preventDefault()} onClick={() => primary && makeSibling(primary)}>
            Sibling
          </button>
          <button type="button" className="quiet-button" disabled={!editable || (!selection.notes.length && !selection.lines.length && !selectedStrokes.length)} onMouseDown={(e) => e.preventDefault()} onClick={removeSelection}>
            Delete
          </button>
          <button type="button" className="quiet-button" onMouseDown={(e) => e.preventDefault()} onClick={() => fit()}>
            Fit
          </button>
        </div>
      ) : null}
      {notice ? (
        <p className="map-notice" role="status">
          {notice}
        </p>
      ) : null}
      {dialog?.kind === 'add' || dialog?.kind === 'link' ? (
        <MapPickerDialog
          title={dialog.kind === 'add' ? 'Add to Map' : 'Link Note'}
          confirm={dialog.kind === 'add' ? 'Add' : 'Link'}
          documents={linkableDocuments(project).filter((d) => d.id !== fragmentId).map((d): MapChoice => ({ kind: 'fragment', ...d }))}
          cast={castChoices(project)}
          onCancel={() => {
            setDialog(null);
            wrapRef.current?.focus({ preventScroll: true });
          }}
          onChoose={(c) => {
            const d = dialog;
            setDialog(null);
            wrapRef.current?.focus({ preventScroll: true });
            if (d.kind === 'add') addChoice(c, d.at);
            else linkNote(d.note, c);
          }}
        />
      ) : null}
      {dialog?.kind === 'cast' ? (
        <LinkCastDialog
          matches={castMatches()}
          onCancel={() => setDialog(null)}
          onLink={(pairs) => {
            setDialog(null);
            linkCast(pairs);
          }}
        />
      ) : null}
      {dialog?.kind === 'scenes' ? (
        <TurnIntoScenesDialog
          content={content}
          selected={new Set(dialog.notes)}
          frames={rest.frames}
          folders={manuscriptFolders(project)}
          mapTitle={title}
          noun={documentNoun(project.type, true)}
          onCancel={() => setDialog(null)}
          onMake={turnIntoScenes}
        />
      ) : null}
      {menu ? (
        <MenuPopup
          label="Map"
          at={menu}
          entries={menuEntries()}
          onClose={(restore) => {
            setMenu(null);
            if (restore) (primary ? noteEls.current.get(primary) : wrapRef.current)?.focus({ preventScroll: true });
          }}
        />
      ) : null}
    </div>
  );
}

/** A note's classes: its size, weight, face, a card's look, and a width of its own. */
function noteClass(n: MapNote, editing: boolean, live: 'fragment' | 'entity' | null): string {
  return [
    'map-note', `size-${n.size}`, n.bold ? 'bold' : '', n.face === 'hand' ? 'hand' : '', n.width !== null ? 'fixed' : 'auto',
    live === 'fragment' ? 'card' : '', live === 'entity' ? 'chip' : '', editing ? 'editing' : '',
  ].filter(Boolean).join(' ');
}

/** A note's own width (the CSSOM's, as the policy allows no style attributes). */
const widthRef = (width: number | null, register: (el: HTMLElement | null) => void) => (el: HTMLElement | null) => {
  if (el) {
    if (width !== null) el.style.setProperty('--note-width', `${width}px`);
    else el.style.removeProperty('--note-width');
  }
  register(el);
};

function NoteFace({ project, note, near, far, card, live, className, register, tabIndex, selected, label, onFocus }: {
  project: Project;
  note: MapNote;
  near: boolean;
  far: boolean;
  /** A document's card: what it shows. */
  card: CardInfo | null;
  /** What the note links to while it's there; a link to something gone is a plain note. */
  live: 'fragment' | 'entity' | null;
  className: string;
  register: (el: HTMLElement | null) => void;
  tabIndex: number;
  selected: boolean;
  label: string;
  onFocus: () => void;
}) {
  const shown = note.link && live ? linkShown(project, note.link, note.text) : null;
  const chipColour = shown?.kind === 'entity' && !shown.missing ? entityCSSColor(shown.color) : null;
  return (
    <div
      ref={widthRef(note.width, (el) => {
        if (el && chipColour) el.style.setProperty('--chip', chipColour);
        register(el);
      })}
      className={className + (card?.trashed ? ' trashed' : '')}
      data-note-id={note.id}
      data-link={note.link && live ? `${note.link.kind}:${note.link.id}` : undefined}
      role="button"
      tabIndex={tabIndex}
      aria-pressed={selected}
      aria-label={label}
      onFocus={onFocus}
    >
      {shown?.kind === 'entity' && !shown.missing ? (
        <span className="map-chip-name">
          <EntityDot color={shown.color} />
          {shown.title}
        </span>
      ) : card ? (
        <>
          <span className="map-card-title">{card.title}</span>
          {near && !far && card.synopsis ? <span className="map-card-synopsis">{card.synopsis}</span> : null}
          {near && !far && card.detail ? <span className="map-card-detail">{card.detail}</span> : null}
          {card.trashed ? <span className="map-card-note">In the Trash</span> : null}
        </>
      ) : (
        <span className="map-note-text">{note.text || ' '}</span>
      )}
    </div>
  );
}

/** A note being typed in: plain text, uncontrolled (what arrives meanwhile waits until the edit ends). */
function NoteEditor({ id, initial, selectAll, className, fixedWidth, register, onInput, onEnd }: {
  id: string;
  initial: string;
  selectAll: boolean;
  className: string;
  fixedWidth: number | null;
  register: (el: HTMLElement | null) => void;
  onInput: (text: string) => void;
  onEnd: (how: 'return' | 'tab' | 'escape' | 'blur', text: string) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const ended = useRef(false);
  const text = () => (ref.current?.innerText ?? '').replace(/\n+$/, '');
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.textContent = initial;
    el.focus({ preventScroll: true });
    const range = document.createRange();
    range.selectNodeContents(el);
    if (!selectAll) range.collapse(false);
    const sel = getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const end = (how: 'return' | 'tab' | 'escape' | 'blur') => {
    if (ended.current) return;
    ended.current = true;
    onEnd(how, text());
  };
  return (
    <div
      ref={widthRef(fixedWidth, (el) => {
        ref.current = el as HTMLDivElement | null;
        register(el);
      })}
      className={className}
      data-note-id={id}
      data-map-note-editor=""
      contentEditable="plaintext-only"
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label="Note"
      spellCheck
      onInput={() => onInput(text())}
      onBlur={() => end('blur')}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.nativeEvent.isComposing) return;
        if (e.key === 'Enter' && e.shiftKey) {
          e.preventDefault();
          document.execCommand('insertText', false, '\n');
        } else if (e.key === 'Enter') {
          e.preventDefault();
          end('return');
        } else if (e.key === 'Tab' && !e.shiftKey) {
          e.preventDefault();
          end('tab');
        } else if (e.key === 'Escape') {
          e.preventDefault();
          end('escape');
        }
      }}
    />
  );
}

/** The bar by the selection: ink, outline, size, bold and face for notes; arrows, dots and a label for a line. */
function StyleBar(p: {
  at: { x: number; y: number; bottom: number; width: number; height: number };
  /** The other notes on screen, which the bar keeps clear of. */
  avoid: MapFrame[];
  notes: MapNote[];
  line: MapLine | null;
  /** A region chosen on its own: ink, Label…, Delete. */
  region: MapRegion | null;
  /** Strokes chosen on their own: ink, Delete. */
  strokes: { ink: InkColor | null | undefined; count: number } | null;
  setStrokesInk: (ink: InkColor | null) => void;
  /** Make Region round the chosen notes (as a ring drawn round them chooses them). */
  makeRegion: (() => void) | null;
  labeling: boolean;
  /** Link… or Unlink for a single note. */
  link: { label: string; onSelect: () => void } | null;
  /** Stack, Make Region, Turn Into Scenes… and the like, under More. */
  more: MenuEntry[];
  setRegions: (fn: (r: MapRegion) => MapRegion) => void;
  removeRegion: () => void;
  inkEntries: (current: InkColor | null | undefined, set: (ink: InkColor | null) => void) => MenuEntry[];
  ink: InkColor | null | undefined;
  outline: MapOutline | undefined;
  size: MapNoteSize | undefined;
  face: MapFace | undefined;
  bold: boolean;
  centre: MenuEntry | null;
  setNotes: (fn: (m: MapDocument, id: string) => void) => void;
  setLines: (fn: (m: MapDocument, id: string) => void) => void;
  chooseFace: (face: MapFace) => void;
  startLabel: () => void;
  endLabel: (text: string | null) => void;
}) {
  const place = (el: HTMLDivElement | null) => {
    if (!el) return;
    const w = el.offsetWidth || 320, h = el.offsetHeight || 36;
    const x = Math.max(8, Math.min(p.at.width - w - 8, p.at.x));
    // Above the selection, else below it, where it covers no other note; else along the top of the paper.
    const clear = (y: number) => y >= 8 && y + h <= p.at.height - 8 && !p.avoid.some((f) => f.x < x + w && f.x + f.width > x && f.y < y + h && f.y + f.height > y);
    const y = [p.at.y - h - 14, p.at.bottom + 14].find(clear) ?? 8;
    el.style.setProperty('left', `${x}px`);
    el.style.setProperty('top', `${y}px`);
  };
  const keepFocus = (e: React.MouseEvent) => e.preventDefault();
  const labelInput = (initial: string) => (
    <input
      className="map-label-input"
      aria-label="Label"
      defaultValue={initial}
      autoFocus
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') p.endLabel((e.target as HTMLInputElement).value);
        else if (e.key === 'Escape') p.endLabel(null);
      }}
      onBlur={(e) => p.endLabel(e.target.value)}
    />
  );
  if (p.strokes && !p.notes.length) {
    return (
      <div className="map-style-bar" role="toolbar" aria-label={p.strokes.count === 1 ? 'Stroke' : 'Strokes'} ref={place}>
        <MenuButton label="Stroke Ink" className="quiet-button map-bar-menu" entries={p.inkEntries(p.strokes.ink, p.setStrokesInk)}>
          <span className={`map-ink-swatch${inkClass(p.strokes.ink ?? null)}`} aria-hidden="true" /> Ink
        </MenuButton>
        <button type="button" className="quiet-button" onMouseDown={keepFocus} onClick={p.removeRegion}>
          Delete
        </button>
      </div>
    );
  }
  if (p.region) {
    const region = p.region;
    return (
      <div className="map-style-bar" role="toolbar" aria-label="Region" ref={place}>
        <MenuButton label="Region Ink" className="quiet-button map-bar-menu" entries={p.inkEntries(region.ink, (ink) => p.setRegions((r) => ({ ...r, ink })))}>
          <span className={`map-ink-swatch${inkClass(region.ink)}`} aria-hidden="true" /> Ink
        </MenuButton>
        {p.labeling ? labelInput(region.label) : (
          <button type="button" className="quiet-button" onClick={p.startLabel}>
            {region.label ? 'Edit Label…' : 'Label…'}
          </button>
        )}
        <button type="button" className="quiet-button" onMouseDown={keepFocus} onClick={p.removeRegion}>
          Delete
        </button>
      </div>
    );
  }
  if (p.line) {
    const line = p.line;
    return (
      <div className="map-style-bar" role="toolbar" aria-label="Line" ref={place}>
        <MenuButton label="Arrows" className="quiet-button map-bar-menu" entries={ARROWS.map((a): MenuEntry => ({ kind: 'radio', label: a.name, checked: line.arrow === a.value, onSelect: () => p.setLines((m, id) => m.setMapLine(id, { arrow: a.value })) }))}>
          {line.arrow === 'none' ? '—' : line.arrow === 'end' ? '→' : '↔'}
        </MenuButton>
        <button type="button" className="quiet-button" aria-pressed={line.style === 'dotted'} onMouseDown={keepFocus} onClick={() => p.setLines((m, id) => m.setMapLine(id, { style: line.style === 'dotted' ? 'solid' : 'dotted' }))}>
          Dotted
        </button>
        <MenuButton label="Line Ink" className="quiet-button map-bar-menu" entries={p.inkEntries(line.ink, (ink) => p.setLines((m, id) => m.setMapLine(id, { ink })))}>
          <span className={`map-ink-swatch${inkClass(line.ink)}`} aria-hidden="true" /> Ink
        </MenuButton>
        {p.labeling ? labelInput(line.label) : (
          <button type="button" className="quiet-button" onClick={p.startLabel}>
            {line.label ? 'Edit Label…' : 'Label…'}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="map-style-bar" role="toolbar" aria-label={p.notes.length === 1 ? 'Note' : 'Notes'} ref={place}>
      <MenuButton label="Ink" className="quiet-button map-bar-menu" entries={p.inkEntries(p.ink, (ink) => p.setNotes((m, id) => m.setMapNote(id, { ink })))}>
        <span className={`map-ink-swatch${inkClass(p.ink ?? null)}`} aria-hidden="true" /> Ink
      </MenuButton>
      <MenuButton label="Outline" className="quiet-button map-bar-menu" entries={OUTLINES.map((o): MenuEntry => ({ kind: 'radio', label: o.name, checked: p.outline === o.value, onSelect: () => p.setNotes((m, id) => m.setMapNote(id, { outline: o.value })) }))}>
        {OUTLINES.find((o) => o.value === p.outline)?.name ?? 'Outline'}
      </MenuButton>
      <span className="map-bar-group" role="group" aria-label="Size">
        {SIZES.map((s) => (
          <button key={s.value} type="button" className="quiet-button" aria-label={s.name} title={s.name} aria-pressed={p.size === s.value} onMouseDown={keepFocus} onClick={() => p.setNotes((m, id) => m.setMapNote(id, { size: s.value }))}>
            {s.short}
          </button>
        ))}
      </span>
      <button type="button" className="quiet-button map-bold" aria-label="Bold" title="Bold" aria-pressed={p.bold} onMouseDown={keepFocus} onClick={() => p.setNotes((m, id) => m.setMapNote(id, { bold: !p.bold }))}>
        B
      </button>
      <span className="map-bar-group" role="group" aria-label="Face">
        <button type="button" className="quiet-button" aria-pressed={p.face === 'serif'} onMouseDown={keepFocus} onClick={() => p.chooseFace('serif')}>
          Serif
        </button>
        <button type="button" className="quiet-button map-hand-button" aria-pressed={p.face === 'hand'} onMouseDown={keepFocus} onClick={() => p.chooseFace('hand')}>
          Hand
        </button>
      </span>
      {p.centre && 'onSelect' in p.centre ? (
        <button type="button" className="quiet-button" onMouseDown={keepFocus} onClick={p.centre.onSelect}>
          {p.centre.label}
        </button>
      ) : null}
      {p.link ? (
        <button type="button" className="quiet-button" onMouseDown={keepFocus} onClick={p.link.onSelect}>
          {p.link.label}
        </button>
      ) : null}
      {p.makeRegion ? (
        <button type="button" className="quiet-button map-make-region" aria-label="Make Region" title="Make Region" onMouseDown={keepFocus} onClick={p.makeRegion}>
          <Icon name="region" />
        </button>
      ) : null}
      {p.more.length ? (
        <MenuButton label="More" className="quiet-button map-bar-menu" title="More" entries={p.more}>
          ⋯
        </MenuButton>
      ) : null}
    </div>
  );
}

/** Clamped zoom, for the header's percentage. */
export const zoomPercent = (k: number) => `${Math.round(clampZoom(k) * 100)}%`;
